import type { Package, Project, ProjectDetails } from "../entities";

/**
 * Represents a package enriched with project information
 */
export interface PackageOption {
  id: string;
  label: string;
  projectId: string | null;
  projectName: string | null;
}

/**
 * Project with packages from /api/projects/{id} response (includes assignedPackages)
 */
export interface ProjectWithPackages extends Project {
  packages: Array<{ id: string; projectId: string }>;
  assignedPackages: ProjectDetails["assignedPackages"];
  assignedUsers: ProjectDetails["assignedUsers"];
}

/**
 * Enriches packages with project information by matching package IDs to projects
 *
 * Strategy: Fetch all tenant projects first via /api/projects?tenantId={id},
 * then fetch packages for each project via /api/projects/{id}/packages,
 * then match packages to those projects in-memory.
 *
 * @param packages - Array of packages from /api/edify/packages
 * @param projectsWithPackages - Array of projects with their packages loaded
 * @returns Array of enriched package options with project information
 */
export function enrichPackagesWithProjects(
  packages: Package[] | null,
  projectsWithPackages: ProjectWithPackages[] | null,
): PackageOption[] {
  if (!packages || packages.length === 0) {
    return [];
  }

  // If no projects available, return empty array (no packages can be assigned)
  if (!projectsWithPackages || projectsWithPackages.length === 0) {
    return [];
  }

  // Create a map of packageId -> project for efficient lookup
  const packageToProjectMap = new Map<string, ProjectWithPackages>();

  for (const project of projectsWithPackages) {
    if (project.packages && Array.isArray(project.packages)) {
      for (const pkg of project.packages) {
        packageToProjectMap.set(pkg.id, project);
      }
    }
  }

  // Enrich packages with project information and filter out unmatched packages
  const result = packages
    .map((pkg) => {
      const matchedProject = packageToProjectMap.get(pkg.id);

      return {
        id: pkg.id,
        label: pkg.name,
        projectId: matchedProject?.id || null,
        projectName: matchedProject?.name || null,
      };
    })
    .filter((pkg) => pkg.projectId !== null); // Only include packages assigned to projects

  return result;
}
